import { useMemo, useState } from "react";
import { Box, Typography, Dialog, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { useVisitPool, toISODate, addDays, useSalesforceLink } from "./data";
import type { SfVisit } from "./data";
import { scoreVisit, fmtPoints } from "./points";
import { usePointsConfig } from "./pointsConfig";
import { distanceMiles, isApproxAccuracy, isInUkOrIreland } from "./geo";
import { SfLink } from "./ui";
import BookVisitModal from "./BookVisitModal";
import type { BookTarget } from "./BookVisitModal";
import type { Located } from "./geo";

/**
 * Day Planner — opened from an empty day, or one with room for a second visit.
 * Scoped to this consultant's own allocated work; the whole pool is the Visit
 * pool page's job. Ordered by due date and nothing else, with location as a
 * column, so the choice stays with the person planning.
 */

export interface PlannerTarget {
    consultantName: string;
    email: string;
    day: Date;
    existing: SfVisit[]; // visits already on this day
}

const SHOWN = 25;

const fmtTime = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const btn = (variant: "dark" | "plain") => ({
    padding: "8px 14px",
    borderRadius: "8px",
    fontSize: "12.5px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: T.font,
    whiteSpace: "nowrap" as const,
    border: variant === "plain" ? `1px solid ${T.border}` : "none",
    background: variant === "dark" ? T.ink : T.card,
    color: variant === "plain" ? T.muted : "#fff",
    "&:hover": { background: variant === "dark" ? T.indigoDeep : T.rowTint },
});

// The Pts column goes with the points flag; header and tracks move together.
const gridFor = (withPoints: boolean) =>
    withPoints
        ? "104px 1.7fr 1.4fr 52px 46px 76px 62px 40px"
        : "104px 1.7fr 1.4fr 46px 76px 62px 40px";

const DayPlannerModal = ({
    target,
    onClose,
    onAddAdmin,
    onGoPool,
}: {
    target: PlannerTarget | null;
    onClose: () => void;
    onAddAdmin: () => void;
    /** Hands off to the Visit pool page, pinned to this consultant. */
    onGoPool?: (email: string, name: string) => void;
}) => {
    const { config, showPoints } = usePointsConfig();

    // Hooks must run before the early return below.
    const [includeOverdue, setIncludeOverdue] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [booking, setBooking] = useState<BookTarget | null>(null);
    const sfLink = useSalesforceLink();

    // Anchored to TODAY, not the clicked day, so every day shares one cached pool —
    // the same key the Overview and Consultant pages already use. Fixed at 90 days;
    // a longer horizon is the Visit pool page's job.
    const today = new Date();
    const pool = useVisitPool(
        toISODate(includeOverdue ? addDays(today, -365) : today),
        toISODate(addDays(today, 90)),
    );

    // Sorting needs only the due date, so score after the slice — not all 7,000.
    const rows = useMemo(() => {
        const alreadyHere = new Set((target?.existing ?? []).map((e) => e.id));
        // Due date first — the planner fills one day, so what is owed binds. Priority
        // is account importance, not urgency, so it only breaks ties.
        const pri = (v: SfVisit) => {
            const n = Number(v.priorityLevel);
            return Number.isFinite(n) ? n : 99;
        };
        return (pool.data ?? [])
            .filter((v) => !alreadyHere.has(v.id))
            .sort(
                (a, b) =>
                    (a.dueDate || "").localeCompare(b.dueDate || "") || pri(a) - pri(b),
            );
    }, [pool.data, target]);

    const shown = useMemo(() => {
        const email = (target?.email || "").toLowerCase();
        return rows.filter((v) => (v.allocatedToEmail || "").toLowerCase() === email);
    }, [rows, target]);

    // The day's booked visits are what distance is measured from. No booking, no distance.
    const bookedOrigins = useMemo<Located[]>(
        () =>
            (target?.existing ?? [])
                .filter(
                    (e) =>
                        e.latitude != null &&
                        e.longitude != null &&
                        isInUkOrIreland(e.latitude as number, e.longitude as number),
                )
                .map((e) => ({ lat: e.latitude as number, lng: e.longitude as number })),
        [target],
    );

    // Booked visits we cannot place: no coordinates, or coordinates outside UK/Ireland.
    const bookedUnplaceable = useMemo(
        () =>
            (target?.existing ?? []).filter(
                (e) =>
                    e.latitude == null ||
                    e.longitude == null ||
                    !isInUkOrIreland(e.latitude as number, e.longitude as number),
            ),
        [target],
    );

    // Distance is measured from the visit already booked that day — that is the
    // pairing question. A day with nothing booked has no origin to measure from.
    const origins = bookedOrigins;

    const visible = useMemo(
        () =>
            shown.slice(0, SHOWN).map((v) => {
                const raw = v.latitude != null && v.longitude != null ? { lat: v.latitude, lng: v.longitude } : null;
                const outsideArea = !!raw && !isInUkOrIreland(raw.lat, raw.lng);
                const here = raw && !outsideArea ? raw : null;
                const approx = isApproxAccuracy(v.geocodeAccuracy);
                const miles = here && origins.length ? Math.min(...origins.map((o) => distanceMiles(o, here))) : null;
                const reason = miles != null
                    ? null
                    : outsideArea
                      ? { label: "outside UK/IE", tip: "This account's coordinates fall outside the UK and Ireland — an overseas site, or a bad geocode. Not treated as pairable work." }
                      : !here
                        ? { label: "unmapped", tip: "This account has no coordinates in Salesforce (BillingLatitude/BillingLongitude are empty), so no distance can be measured to it." }
                        : {
                              label: "no origin",
                              tip: "Nothing is booked this day, so there is no starting point to measure from. Distances appear once the day has a visit on it.",
                          };
                return {
                    v,
                    miles,
                    approx,
                    reason,
                    points: scoreVisit(
                        {
                            subActivity: v.subActivity,
                            name: v.name,
                            visitCategory: v.visitCategory,
                            industry: v.industry,
                            fraRiskLevel: v.fraRiskLevel,
                            highRiskFra: v.highRiskFra,
                        },
                        config,
                        // Unbooked work has no On Site / Remote flag yet.
                        { assumeOnSiteIfUnset: true },
                    ).points,
                };
            }),
        [shown, config, origins],
    );


    if (!target) return null;
    const firstName = target.consultantName.split(" ")[0];

    const dateLabel = target.day.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long" });

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px", fontFamily: T.font, background: T.card } }}>
            <Box sx={{ padding: "20px 24px", fontFamily: T.font, color: T.ink2 }}>
                <Typography sx={{ fontSize: "17px", fontWeight: 800, color: T.ink2 }}>Plan this day</Typography>
                <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "2px" }}>
                    {target.consultantName} · {dateLabel}
                    {target.existing.length > 0 &&
                        ` · already has ${target.existing
                            .map((v) => `${v.accountName} ${fmtTime(v.visitStartDate)}`)
                            .join(", ")}`}
                </Typography>

                {pool.isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: "28px" }}>
                        <CircularProgress size={22} />
                    </Box>
                )}
                {pool.isError && (
                    <Typography sx={{ fontSize: "12.5px", color: T.amber, mt: "16px" }}>
                        Couldn't load the visit pool.
                    </Typography>
                )}

                {!pool.isLoading && !pool.isError && (
                    <Box sx={{ mt: "16px", border: `1px solid ${T.border}`, borderRadius: "10px", overflow: "hidden" }}>
                        <Box sx={{ display: "flex", alignItems: "center", background: T.rowTint, borderBottom: `1px solid ${T.borderSoft}` }}>
                            <Typography
                                sx={{
                                    padding: "10px 15px",
                                    fontSize: "12.5px",
                                    fontWeight: 800,
                                    color: T.ink2,
                                    borderBottom: `2px solid ${T.purple}`,
                                }}
                            >
                                {`Allocated to ${firstName}`}
                                <Box component="span" sx={{ color: T.muted, fontWeight: 700, ml: "6px" }}>
                                    {shown.length}
                                </Box>
                            </Typography>
                            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "10px", pr: "11px" }}>
                                <Box
                                    component="button"
                                    onClick={() => setIncludeOverdue((x) => !x)}
                                    aria-pressed={includeOverdue}
                                    title="Overdue means due before today. Off by default — it is most of the pool, and the slow part."
                                    sx={{
                                        border: `1px solid ${includeOverdue ? T.red : T.border}`,
                                        borderRadius: "999px",
                                        padding: "3px 10px",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        fontFamily: T.font,
                                        cursor: "pointer",
                                        background: T.card,
                                        color: includeOverdue ? T.red : T.muted,
                                    }}
                                >
                                    {includeOverdue ? "Overdue shown" : "Include overdue"}
                                </Box>
                                <Typography
                                    sx={{ fontSize: "11.5px", color: T.muted }}
                                    title="Soonest due date first; where two fall together the higher priority level leads."
                                >
                                    Soonest due first
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ overflowX: "auto" }}>
                            <Box sx={{ minWidth: "620px" }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), padding: "6px 11px", borderTop: `1px solid ${T.borderSoft}` }}>
                                    {["Due", "Account", "Visit type", ...(showPoints ? ["Pts"] : []), "Pri", "Distance", "", ""].map((h, i) => (
                                        <Typography key={i} sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted }}>
                                            {h}
                                        </Typography>
                                    ))}
                                </Box>
                                {visible.map(({ v, points, miles, approx, reason }) => {
                                    const due = v.dueDate ? new Date(v.dueDate) : null;
                                    const overdue = !!due && due < target.day;
                                    return (
                                        <Box
                                            key={v.id}
                                            onClick={() => setSelectedId((cur) => (cur === v.id ? null : v.id))}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: gridFor(showPoints),
                                                alignItems: "center",
                                                padding: "7px 11px",
                                                borderTop: `1px solid ${T.borderSoft}`,
                                                cursor: "pointer",
                                                background: selectedId === v.id ? T.purpleTint : "transparent",
                                                "&:hover": { background: selectedId === v.id ? T.purpleTint : T.rowTint },
                                            }}
                                        >
                                            <Typography sx={{ fontSize: "12px", fontWeight: overdue ? 800 : 600, color: overdue ? T.red : T.ink2 }}>
                                                {due
                                                    ? due.toLocaleDateString("en-GB", {
                                                          day: "2-digit",
                                                          month: "short",
                                                          year: "numeric",
                                                      })
                                                    : "—"}
                                            </Typography>
                                            <Typography sx={{ fontSize: "12.5px", color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                                {v.accountName || "—"}
                                            </Typography>
                                            <Typography sx={{ fontSize: "11.5px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                                {v.subActivity || v.name}
                                                {v.locationGroup ? ` · ${v.locationGroup}` : ""}
                                            </Typography>
                                            {showPoints && (
                                                <Typography sx={{ fontSize: "12px", fontWeight: 800, color: points == null ? T.amber : T.purpleDeep, fontVariantNumeric: "tabular-nums" }}>
                                                    {points == null ? "—" : fmtPoints(points)}
                                                </Typography>
                                            )}
                                            <Typography
                                                title={
                                                    v.priorityLevel == null || v.priorityLevel === ""
                                                        ? "No priority level set on this visit in Salesforce"
                                                        : `Priority level ${v.priorityLevel}`
                                                }
                                                sx={{
                                                    fontSize: "12px",
                                                    fontWeight: 800,
                                                    fontVariantNumeric: "tabular-nums",
                                                    color: v.priorityLevel == null || v.priorityLevel === "" ? T.muted2 : T.ink2,
                                                }}
                                            >
                                                {v.priorityLevel == null || v.priorityLevel === "" ? "—" : v.priorityLevel}
                                            </Typography>
                                            <Typography
                                                title={
                                                    reason
                                                        ? reason.tip
                                                        : `Straight line from the visit booked this day — not drive time${approx ? ". Account is geocoded to city level only, so treat it as approximate" : ""}`
                                                }
                                                sx={{
                                                    fontSize: reason ? "10px" : "11.5px",
                                                    fontWeight: reason ? 600 : 700,
                                                    textAlign: "right",
                                                    fontVariantNumeric: "tabular-nums",
                                                    color: reason ? T.muted2 : approx ? T.amber : T.purpleDeep,
                                                    cursor: "help",
                                                    pr: "12px",
                                                }}
                                            >
                                                {reason ? reason.label : `${approx ? "~" : ""}${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`}
                                            </Typography>
                                            <Box
                                                component="button"
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    setBooking({ visit: v, day: target.day });
                                                }}
                                                title={`Book ${v.accountName || "this visit"} for this day`}
                                                sx={{
                                                    border: `1px solid ${T.border}`,
                                                    borderRadius: "7px",
                                                    background: T.card,
                                                    color: T.purpleDeep,
                                                    padding: "4px 9px",
                                                    fontSize: "11px",
                                                    fontWeight: 800,
                                                    fontFamily: T.font,
                                                    cursor: "pointer",
                                                    "&:hover": { background: T.purpleTint },
                                                }}
                                            >
                                                Book
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                <SfLink href={sfLink("Visit__c", v.id)} label={`Open ${v.accountName || "this visit"} in Salesforce`} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                                {shown.length === 0 && (
                                    <Typography sx={{ fontSize: "12.5px", color: T.muted, padding: "12px 11px" }}>
                                        {includeOverdue
                                            ? `Nothing owed for ${firstName} in this window.`
                                            : `Nothing due for ${firstName} in the next 90 days — try including overdue.`}
                                    </Typography>
                                )}
                                {(shown.length > SHOWN || !!onGoPool) && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 11px", borderTop: `1px solid ${T.borderSoft}` }}>
                                        <Typography sx={{ fontSize: "11px", color: T.muted2 }}>
                                            {shown.length > SHOWN
                                                ? `Showing the ${SHOWN} soonest of ${shown.length} work items.`
                                                : `Showing all ${shown.length} work item${shown.length === 1 ? "" : "s"}.`}
                                        </Typography>
                                        {onGoPool && (
                                            <Box
                                                component="button"
                                                onClick={() => {
                                                    onGoPool(target.email, target.consultantName);
                                                    onClose();
                                                }}
                                                title={`Opens the Visit pool page showing only ${firstName}'s owed work, where the horizon goes wider`}
                                                sx={{
                                                    ml: "auto",
                                                    border: `1px solid ${T.border}`,
                                                    borderRadius: "8px",
                                                    background: T.card,
                                                    color: T.purpleDeep,
                                                    padding: "5px 11px",
                                                    fontSize: "11.5px",
                                                    fontWeight: 800,
                                                    fontFamily: T.font,
                                                    cursor: "pointer",
                                                    whiteSpace: "nowrap",
                                                    "&:hover": { background: T.rowTint },
                                                }}
                                            >
                                                See all in visit pool →
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}

                {!pool.isLoading && !pool.isError && bookedUnplaceable.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: "8px",
                            mt: "12px",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            background: T.amberTint,
                            border: `1px solid ${T.amberBorder}`,
                            color: T.amber,
                        }}
                    >
                        <Box sx={{ fontWeight: 800, flexShrink: 0 }}>!</Box>
                        <Typography sx={{ fontSize: "11.5px", lineHeight: 1.5 }}>
                            <b>No address on the visit already booked this day</b>
                            {` — ${bookedUnplaceable.map((e) => e.accountName || "unnamed account").join(", ")}. `}
                            Salesforce holds no usable coordinates for it, so no distance can be measured from it.
                            Add the billing address to the account in Salesforce to fix this.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "16px" }}>
                    {target.existing.length === 0 && (
                        <Box component="button" onClick={onAddAdmin} title="Adds it straight to the board" sx={btn("dark")}>
                            Add admin day
                        </Box>
                    )}
                    <Typography sx={{ fontSize: "11px", color: T.muted2, ml: "4px" }}>
                        Points assume an on-site visit — unbooked work has no on-site/remote flag. Book checks the
                        day for clashes, then hands the details to Salesforce — there is no visit-write API yet.
                    </Typography>
                    <Box sx={{ marginLeft: "auto" }}>
                        <Box component="button" onClick={onClose} sx={btn("plain")}>
                            Back
                        </Box>
                    </Box>
                </Box>
            </Box>
            <BookVisitModal target={booking} onClose={() => setBooking(null)} />
        </Dialog>
    );
};

export default DayPlannerModal;
